<template>
  <div ref="rootEl" class="accordion">
    <!-- TITLE -->
    <div
      class="accordion__title"
      :class="{ 'accordion__title--left-arrow': arrowLeft }"
      @click="toggleAccordion"
      @mousedown.prevent
    >
      {{ title }}
      <span
        class="accordion__title-arrow"
        :class="[
          `icon-chevron-${isActive ? 'up' : 'down'}`,
          { 'accordion__title-arrow--left': arrowLeft }
        ]"
      />
    </div>

    <!-- BODY -->
    <transition
      name="accordion"
      @before-enter="transitionBeforeEnter"
      @enter="transitionEnter"
      @after-enter="transitionAfterEnterWrapper"
      @before-leave="transitionBeforeLeave"
      @leave="transitionLeave"
    >
      <div v-show="isActive" class="accordion__body">
        <slot />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useTransition } from '@/composables/useTransition'

type Props = {
  title: string
  arrowPosition?: 'left' | 'right'
  isOpened?: boolean
}
type Emits = {
  (e: 'change', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  arrowPosition: 'right',
  isOpened: false
})
const emit = defineEmits<Emits>()

const rootEl = useTemplateRef<HTMLDivElement>('rootEl')
const isActive = shallowRef(props.isOpened)

const {
  transitionBeforeEnter,
  transitionEnter,
  transitionAfterEnter,
  transitionBeforeLeave,
  transitionLeave
} = useTransition()

const arrowLeft = computed(() => {
  return props.arrowPosition === 'left'
})

watch(() => props.isOpened, (value: boolean) => {
  isActive.value = value
})

const transitionAfterEnterWrapper = (el: Element) => {
  transitionAfterEnter(el)

  const windowHeight = Math.min(
    document.documentElement.clientHeight,
    rootEl.value!.offsetParent!.clientHeight
  )
  const bottomEl = rootEl.value!.getBoundingClientRect().bottom

  if (windowHeight > bottomEl) {
    return
  }

  rootEl.value!.scrollIntoView({
    behavior: 'smooth',
    block: 'end'
  })
}

const toggleAccordion = () => {
  isActive.value = !isActive.value
  emit('change', isActive.value)
}
</script>

<style lang="scss" scoped>
.accordion {
  $root: &;

  &__title {
    position: relative;
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 2.1rem;
    padding-right: 3.4rem;
    cursor: pointer;

    &--left-arrow {
      padding-right: 0;
      padding-left: 3rem;
      font-size: 1.6rem;
      line-height: 1.9rem;
      font-weight: 400;
    }
  }

  &__title-arrow {
    position: absolute;
    top: .3rem;
    right: 0;
    color: #8A8A8A;

    &--left {
      color: $font-color;
      left: 0;
      right: initial;
    }
  }

  &__body {
    padding-top: 1.6rem;
    position: relative;
    left: -1rem;
    right: -1rem;
    width: calc(100% + 2rem);
    padding-left: 1rem;
    padding-right: 1rem;
  }

  &-leave-active {
    padding-top: 0;
    padding-bottom: 0;
  }

  &-leave-active,
  &-enter-active {
    overflow: hidden;
    transition: .2s ease;
    transition-property: max-height, padding;
  }
}
</style>
